import { Alert, Box, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/if"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useRefresh } from "lib/@hooks/useRefresh"
import ReactMarkdown from "react-markdown"
import { ResolutionStatusChip } from "./resolution-status-chip"
import { ChangeItem } from "./change-item"
import { LegislationList } from "./legislation-list"
import { AcceptAllButton } from "./accept-all-button"

export function ResolutionContent() {
    const { target: resolution, onAcceptAll } = useBoundContext()
    const refresh = useRefresh()
    const changes = resolution?.proposedChanges ?? []
    const allDecided = changes.length > 0 && changes.every((c) => c.decision !== null)
    const undecidedCount = changes.filter((c) => c.decision === null).length
    const hasUndecided = !allDecided && changes.length > 0

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h5">
                    {resolution.code} — {resolution.title}
                </Typography>
                <ResolutionStatusChip status={resolution.status} />
            </Stack>

            <If truthy={resolution.inconsistencyContent}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "background.neutral",
                        "& h1,& h2,& h3,& h4": { mt: 1.5, mb: 0.5 },
                        "& p": { mt: 0, mb: 1 },
                        "& ul,& ol": { pl: 2.5, mb: 1 },
                        "& li": { mb: 0.5 },
                    }}
                >
                    <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                        Identified Inconsistencies
                    </Typography>
                    <ReactMarkdown>{resolution.inconsistencyContent}</ReactMarkdown>
                </Box>
            </If>

            <If truthy={resolution.errorMessage}>
                <Alert severity="error">{resolution.errorMessage}</Alert>
            </If>

            <If truthy={resolution.status === "processing"}>
                <Alert severity="info">
                    AI is currently generating resolution proposals. Refresh to check progress.
                </Alert>
            </If>

            <If truthy={changes.length === 0 && resolution.status === "complete"}>
                <Alert severity="success">
                    No changes proposed. The AI found no concrete improvements to make based on the identified
                    inconsistencies.
                </Alert>
            </If>

            <If truthy={hasUndecided}>
                <Alert severity="warning">
                    {undecidedCount} change{undecidedCount !== 1 ? "s" : ""} still need a decision before you can apply.
                </Alert>
            </If>

            <If truthy={changes.length > 0}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <AcceptAllButton changes={changes} onAcceptAll={onAcceptAll} />
                </Stack>
            </If>

            <Bound target={resolution} refresh={refresh}>
                <Repeat list={changes} item={<ChangeItem />} keyFn={changeKey} />
            </Bound>

            <LegislationList legislation={resolution.legislation} />
        </Stack>
    )
}

function changeKey(c) {
    return c.id
}
